/**
 * Przycisk „Na górę" — szybki powrót na początek długiej strony.
 *
 * Prośba właściciela (2026-09-28): na widokach z dużą ilością danych (Kierowcy
 * ~1066 i Pojazdy ~448 po imporcie z GAiT, Plan służb) przewijanie z powrotem do
 * filtrów trwało. Jeden przycisk w układzie panelu zamiast osobnego na każdej
 * stronie — działa wszędzie, gdzie przewija się główna treść (`<main>` w AppLayout).
 *
 * Pojawia się dopiero po przewinięciu o ekran w dół, żeby nie wisiał na krótkich
 * stronach. Stoi NAD przyciskiem „Zgłoś uwagę" (prawy dolny róg, 24 px + 48 px
 * + odstęp), a z-index ma niższy niż okna modalne, więc te go przykrywają.
 */
import { useEffect, useState, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

export function ScrollToTopButton({ targetRef }: { targetRef: RefObject<HTMLElement | null> }) {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    // Próg = wysokość okna: dopiero wtedy góra strony (filtry) znika z widoku.
    const check = () => setVisible(el.scrollTop > Math.max(400, el.clientHeight));
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
    // `pathname`: po przejściu na inną stronę pozycja przewinięcia się zmienia,
    // a zdarzenie `scroll` nie musi przyjść — sprawdzamy wtedy od nowa.
  }, [targetRef, pathname]);

  return (
    <button
      type="button"
      onClick={() => targetRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
      title="Na górę strony"
      aria-label="Przewiń na górę strony"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={cn(
        'fixed bottom-[84px] right-[26px] z-40 flex h-11 w-11 items-center justify-center rounded-full',
        'border border-border bg-card/95 text-foreground shadow-lg backdrop-blur-sm',
        'transition-all duration-200 hover:-translate-y-0.5 hover:bg-muted',
        visible ? 'opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
      )}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}
